/**
 * 주문 핵심 CRUD 함수
 * 기존 order.ts에서 분리
 */

import { supabase } from '@/lib/supabase';
import type { Order } from '@/types';

/**
 * 주문 ID로 주문 조회
 */
export async function getOrderById(
  orderId: string
): Promise<Order | null> {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        items:order_items(
          *,
          product:products!product_id(id, name, original_price),
          offer:product_offers!offer_id(id, offer_type, discount_rate, discounted_price)
        ),
        restaurant:restaurants(id, name, address, phone),
        payment:payments(id, amount, status, transaction_id, paid_at, approved_at)
      `)
      .eq('id', orderId)
      .single();

    if (error || !data) {
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error fetching order:', error);
    return null;
  }
}

/**
 * 사용자의 주문 목록 조회 (결제 완료된 주문만)
 */
export async function getUserOrders(
  userId: string,
  options?: {
    limit?: number;
    offset?: number;
    status?: Order['status'];
    orderType?: string;
  }
): Promise<{ orders: Order[]; total: number }> {
  try {
    
    let query = supabase
      .from('orders')
      .select(`
        *,
        items:order_items(
          *,
          product:products!product_id(id, name, original_price),
          offer:product_offers!offer_id(id, offer_type, discount_rate, discounted_price)
        ),
        restaurant:restaurants(id, name, address, phone),
        payment:payments(id, amount, status, transaction_id, paid_at, approved_at)
      `, { count: 'exact' })
      .eq('buyer_id', userId)
      // 결제 완료된 주문만 조회
      .in('payment_status', ['PAID', 'REFUNDED'])
      .order('created_at', { ascending: false });

    if (options?.status) {
      query = query.eq('status', options.status);
    }

    if (options?.orderType) {
      query = query.eq('order_type', options.orderType);
    }

    if (options?.limit) {
      query = query.limit(options.limit);
    }

    if (options?.offset) {
      query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching user orders:', error);
      return { orders: [], total: 0 };
    }


    return { orders: data || [], total: count || 0 };
  } catch (error) {
    console.error('Error in getUserOrders:', error);
    return { orders: [], total: 0 };
  }
}

/**
 * 매장의 주문 목록 조회 (결제 완료된 주문만)
 */
export async function getRestaurantOrders(
  restaurantId: string,
  options?: {
    limit?: number;
    offset?: number;
    status?: Order['status'];
    date?: Date;
  }
): Promise<{ orders: Order[]; total: number }> {
  try {
    let query = supabase
      .from('orders')
      .select(`
        *,
        items:order_items(
          *,
          product:products!product_id(id, name, original_price),
          offer:product_offers!offer_id(id, offer_type, discount_rate, discounted_price)
        ),
        buyer:buyers(id, name, phone),
        payment:payments(id, amount, status, transaction_id, paid_at, approved_at)
      `, { count: 'exact' })
      .eq('restaurant_id', restaurantId)
      // 결제 완료된 주문만 조회
      .in('payment_status', ['PAID', 'REFUNDED'])
      .order('created_at', { ascending: false });

    if (options?.status) {
      query = query.eq('status', options.status);
    }

    if (options?.date) {
      const startOfDay = new Date(options.date);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(options.date);
      endOfDay.setHours(23, 59, 59, 999);
      
      query = query
        .gte('created_at', startOfDay.toISOString())
        .lte('created_at', endOfDay.toISOString());
    }

    if (options?.limit) {
      query = query.limit(options.limit);
    }

    if (options?.offset) {
      query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching restaurant orders:', error);
      return { orders: [], total: 0 };
    }

    return { orders: data || [], total: count || 0 };
  } catch (error) {
    console.error('Error in getRestaurantOrders:', error);
    return { orders: [], total: 0 };
  }
}

/**
 * 최근 주문 조회 (결제 완료된 주문만)
 */
export async function getRecentOrders(
  userId: string,
  limit: number = 5
): Promise<Order[]> {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        items:order_items(
          *,
          product:products!product_id(id, name, original_price),
          offer:product_offers!offer_id(id, offer_type, discount_rate, discounted_price)
        ),
        restaurant:restaurants(id, name, address, phone)
      `)
      .eq('buyer_id', userId)
      .in('payment_status', ['PAID', 'REFUNDED'])
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching recent orders:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getRecentOrders:', error);
    return [];
  }
}

/**
 * 활성 주문 조회 (PENDING, ACCEPTED, READY) - 결제 완료된 주문만
 */
export async function getActiveOrders(
  userId: string
): Promise<Order[]> {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        items:order_items(
          *,
          product:products!product_id(id, name, original_price),
          offer:product_offers!offer_id(id, offer_type, discount_rate, discounted_price)
        ),
        restaurant:restaurants(id, name, address, phone),
        payment:payments(id, amount, status, transaction_id, paid_at, approved_at)
      `)
      .eq('buyer_id', userId)
      .eq('payment_status', 'PAID')
      .in('status', ['PENDING', 'ACCEPTED', 'READY'])
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching active orders:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getActiveOrders:', error);
    return [];
  }
}

/**
 * 주문 삭제 (테스트용)
 */
export async function deleteOrder(
  orderId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Delete order items first
    await supabase
      .from('order_items')
      .delete()
      .eq('order_id', orderId);

    // Delete payment records
    await supabase
      .from('payments')
      .delete()
      .eq('order_id', orderId);

    // Delete the order
    const { error } = await supabase
      .from('orders')
      .delete()
      .eq('id', orderId);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Error deleting order:', error);
    return { success: false, error: '주문 삭제 중 오류가 발생했습니다.' };
  }
}
